/**
 * CompCard Component
 * Displays individual film comp information with actions
 */

import React from 'react';
import { 
  Card, 
  CardContent, 
  CardActions, 
  Typography, 
  Box, 
  Chip, 
  Button, 
  LinearProgress,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  CalendarToday,
  Person
} from '@mui/icons-material';
import { 
  formatDate, 
  formatCompType,
  formatAssignmentReason,
  formatFilmTitle
} from '../../lib/utils/compFormatters';

const CompCard = ({ 
  comp, 
  type, 
  onEdit, 
  onDelete, 
  onViewDetails,
  viewMode = 'grid'
}) => {
  // NOTE: Modeled comps have been temporarily removed.
  // Only greenlight comps are supported now.
  const compTypeConfig = formatCompType('greenlight');
  
  // Handle card click
  const handleCardClick = () => {
    if (onViewDetails) {
      onViewDetails(comp);
    }
  };

  // Handle edit click
  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(comp);
    }
  };

  // Handle delete click
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(comp);
    }
  };

  // Handle view details click
  const handleViewDetailsClick = (e) => {
    e.stopPropagation();
    if (onViewDetails) {
      onViewDetails(comp);
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        cursor: onViewDetails ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          borderColor: compTypeConfig.color + '40'
        }
      }}
      onClick={handleCardClick}
    >
      {/* Header */}
      <CardContent sx={{ pb: 1, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant={viewMode === 'list' ? 'h6' : 'subtitle1'} 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {formatFilmTitle(comp.compFilmTitle, viewMode === 'list' ? 80 : 50)}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontFamily: 'monospace' }}
            >
              {comp.compFilmId}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            <Chip
              icon={compTypeConfig.icon}
              label={compTypeConfig.label}
              size="small"
              sx={{
                backgroundColor: compTypeConfig.color + '15',
                color: compTypeConfig.color,
                border: `1px solid ${compTypeConfig.color}30`,
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          </Box>
        </Box>


        {/* Assignment Info */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Person sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Assigned by: <strong>{comp.assignedBy || 'Unknown'}</strong>
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarToday sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(comp.assignedAt)}
            </Typography>
          </Box>

          {comp.reason && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Reason:
              </Typography>
              <Typography 
                variant="caption" 
                color="text.primary"
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: viewMode === 'list' ? 3 : 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {formatAssignmentReason(comp.reason, viewMode === 'list' ? 150 : 100)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Date Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarToday sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            Assigned: {formatDate(comp.assignedAt)}
          </Typography>
        </Box>
      </CardContent>

      {/* Actions */}
      <Divider />
      <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
        <Button
          size="small"
          startIcon={<Visibility />}
          onClick={handleViewDetailsClick}
          sx={{ 
            fontSize: '0.75rem',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          View Details
        </Button>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {/* Edit button */}
          {onEdit && (
            <Tooltip title="Edit comp">
              <IconButton 
                size="small" 
                onClick={handleEditClick}
                sx={{ color: 'primary.main' }}
              >
                <Edit sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Delete button */}
          {onDelete && (
            <Tooltip title="Delete comp">
              <IconButton 
                size="small" 
                onClick={handleDeleteClick}
                sx={{ color: 'error.main' }}
              >
                <Delete sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

export default CompCard;
